import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaCheck, FaCode, FaCopy } from "react-icons/fa";
import { apihubGet } from "../../api/apihub";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { businessPath } from "../../config/businessMenu";
import { allowDevMockFallback } from "../../utils/devMockFallback";
import { isApprovedTemplate } from "../../utils/templatePreview";
import CampaignTemplatePicker, {
  CampaignTemplatePreview,
} from "./CampaignTemplatePicker";

const MOCK_TEMPLATES = [
  {
    _id: "69f6ffb2b1ae76dbff45c051",
    template_id: "4513437452222207",
    name: "hello_world",
    category: "MARKETING",
    template_status: "APPROVED",
  },
];

const STEPS = [
  { n: "1", title: "Name the campaign", text: "Use a name your team will recognize in reports." },
  { n: "2", title: "Choose a template", text: "Pick an approved WhatsApp template to send from your API." },
  { n: "3", title: "Trigger from your app", text: "Call sendTemplate with your API key, template, and recipient number." },
];

const ApiCampaign = () => {
  const { projectId, phoneNoId } = useProjectPhone();
  const [campaignName, setCampaignName] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const fetchTemplates = useCallback(async () => {
    if (!phoneNoId) return;
    setLoadingTemplates(true);
    try {
      const res = await apihubGet(
        `/getInteractedCustomerDetail/gettotaltemplatesbyphonenumberid/${phoneNoId}?page=1&limit=100&sortBy=createdAt&sortOrder=desc`,
      );
      const list = res?.data || res?.templates || [];
      const next =
        Array.isArray(list) && list.length
          ? list
          : allowDevMockFallback()
            ? MOCK_TEMPLATES
            : [];
      setTemplates(next);
      setSelectedTemplate((prev) =>
        prev && isApprovedTemplate(prev) ? prev : null,
      );
    } catch {
      setTemplates(allowDevMockFallback() ? MOCK_TEMPLATES : []);
      setSelectedTemplate(null);
    } finally {
      setLoadingTemplates(false);
    }
  }, [phoneNoId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const curlExample = useMemo(() => {
    const templateName = selectedTemplate?.name || "welcome";
    const templateId = selectedTemplate?.template_id || selectedTemplate?._id || "TEMPLATE_ID";
    return `POST /getInteractedCustomerDetail/sendTemplate
apiKey: YOUR_API_KEY
Content-Type: application/json

{
  "id": "${templateId}",
  "name": "${templateName}",
  "to": "919876543210",
  "input_var_1": "Rahul"
}`;
  }, [selectedTemplate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!campaignName.trim()) {
      setError("Enter a campaign name.");
      setSubmitted(false);
      return;
    }
    if (!selectedTemplate) {
      setError("Select an approved WhatsApp template.");
      setSubmitted(false);
      return;
    }
    setError("");
    setSubmitted(true);
  };

  const copyCurl = () => {
    navigator.clipboard.writeText(curlExample).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="campaign-detail-page biz-apicampaign-page">
      <header className="campaign-detail-topbar">
        <div className="campaign-detail-topbar-left">
          <Link
            to={businessPath(projectId, "fetchingallcampaign")}
            className="campaign-detail-back-icon"
            aria-label="Back to campaigns"
          >
            <FaArrowLeft />
          </Link>
          <div className="campaign-detail-heading">
            <h1>API Campaign</h1>
            <p className="campaign-detail-subtitle">
              Set up a campaign you can trigger from your own systems.
            </p>
          </div>
        </div>
        <Link
          to={businessPath(projectId, "apikeygenerate")}
          className="campaign-detail-refresh"
        >
          <FaCode />
          API key & docs
        </Link>
      </header>

      <div className="campaign-detail-body">
        <ol className="biz-apicampaign-steps">
          {STEPS.map((step) => (
            <li key={step.n}>
              <span>{step.n}</span>
              <div>
                <strong>{step.title}</strong>
                <p>{step.text}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="campaign-detail-panels">
          <form className="campaign-detail-card" onSubmit={handleSubmit}>
            <h2>Campaign setup</h2>

            {error && <p className="biz-form-error">{error}</p>}
            {submitted && (
              <p className="biz-form-success">
                <FaCheck /> Setup saved. Use the API request on the right to send
                this template. Results appear in{" "}
                <Link to={businessPath(projectId, "fetchingallcampaign")}>
                  Campaign Report
                </Link>
                .
              </p>
            )}

            <label className="biz-apicampaign-label" htmlFor="campaignName">
              Campaign name
            </label>
            <input
              id="campaignName"
              type="text"
              className="biz-apicampaign-input"
              placeholder="e.g. Onboarding — API"
              value={campaignName}
              onChange={(e) => {
                setCampaignName(e.target.value);
                setError("");
                setSubmitted(false);
              }}
            />

            <CampaignTemplatePicker
              templates={templates}
              selectedTemplate={selectedTemplate}
              loading={loadingTemplates}
              showPreview={false}
              onSelect={(tpl) => {
                setSelectedTemplate(tpl);
                setError("");
                setSubmitted(false);
              }}
            />

            <div className="biz-apicampaign-actions">
              <button type="submit" className="login-submit">
                Save setup
              </button>
            </div>
          </form>

          <div className="biz-apicampaign-side">
            <section className="campaign-detail-card biz-apicampaign-preview-card">
              <CampaignTemplatePreview selectedTemplate={selectedTemplate} />
            </section>

            <section className="campaign-detail-card">
              <div className="campaign-detail-preview-head">
                <h2>Send with API</h2>
                <button
                  type="button"
                  className="biz-apicampaign-copy"
                  onClick={copyCurl}
                >
                  <FaCopy />
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="biz-apicampaign-guide">
                Recipients are not uploaded here. Your application sends each
                number with your API key.
              </p>
              <pre className="biz-apicampaign-curl">{curlExample}</pre>
              <p className="biz-apicampaign-guide">
                Generate or copy your key from the{" "}
                <Link to={businessPath(projectId, "apikeygenerate")}>
                  API key
                </Link>{" "}
                page. Use <code>input_var_1</code> to <code>input_var_3</code>{" "}
                only when the template has body variables.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiCampaign;
